import { useEffect, useRef } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { ENCDE_SECRET } from "store/api/constApi";
import { useAppSelector } from "store/hooks";
import { usePortalVanityUrl } from "store/slices/dashboardSlice";
import { RootState } from "store/store";
import { encode } from "utils/HelperFunctions";
import { decodeLegacyUrlValue } from "utils/legacyUrlDecoder";
import { INVALID_PATH, invalidRouteState } from "utils/invalidRoute";

const LEGACY_PAYMENT_LINK_TYPES = new Set(["sms_dbu", "email_dbu"]);
const LEGACY_ID_PATTERN = /^[1-9]\d{0,19}$/;
const LEGACY_PAYMENT_LINK_ID_PATTERN = /^pid_([1-9]\d{0,19})$/;

export type LegacyPaymentLinkParams = {
  clusterId?: string;
  leadId?: string;
  paymentLinkId?: string;
  plink?: string;
};

type RedirectUrlOptions = {
  baseUrl: string;
  origin: string;
  search?: string;
  hash?: string;
  timestamp?: number;
  legacyCodes?: string;
};

export const createLegacyPaymentLinkSlug = (
  { clusterId, leadId, paymentLinkId, plink }: LegacyPaymentLinkParams,
  timestamp: number = Date.now(),
  legacyCodes: string = ENCDE_SECRET
): string | null => {
  const decodedClusterId = decodeLegacyUrlValue(clusterId, legacyCodes);
  const decodedLeadId = decodeLegacyUrlValue(leadId, legacyCodes);
  const decodedPaymentLinkId = decodeLegacyUrlValue(paymentLinkId, legacyCodes);
  const decodedLinkType = decodeLegacyUrlValue(plink, legacyCodes);
  const normalizedPaymentLinkId = LEGACY_ID_PATTERN.test(
    decodedPaymentLinkId || ""
  )
    ? decodedPaymentLinkId
    : decodedPaymentLinkId?.match(LEGACY_PAYMENT_LINK_ID_PATTERN)?.[1] || null;
  const hasValidIds = [decodedClusterId, decodedLeadId].every(
    (value) => typeof value === "string" && LEGACY_ID_PATTERN.test(value)
  );

  if (
    !hasValidIds ||
    !normalizedPaymentLinkId ||
    !decodedLinkType ||
    !LEGACY_PAYMENT_LINK_TYPES.has(decodedLinkType) ||
    !Number.isSafeInteger(timestamp) ||
    timestamp <= 0
  ) {
    return null;
  }

  return encode(
    `${normalizedPaymentLinkId}_${decodedClusterId}_${timestamp}`,
    "pl_"
  );
};

export const buildLegacyPaymentLinkRedirectUrl = (
  params: LegacyPaymentLinkParams,
  {
    baseUrl,
    origin,
    search = "",
    hash = "",
    timestamp,
    legacyCodes,
  }: RedirectUrlOptions
): string | null => {
  const slug = createLegacyPaymentLinkSlug(params, timestamp, legacyCodes);
  if (!slug) return null;

  const canonicalBaseUrl = new URL(baseUrl || "/", origin);
  const destination = new URL(
    `payment_link/${encodeURIComponent(slug)}`,
    canonicalBaseUrl
  );

  destination.search = search;
  destination.hash = hash;

  return destination.toString();
};

const LegacyPaymentLinkRedirect = () => {
  const params = useParams<LegacyPaymentLinkParams>();
  const location = useLocation();
  const vanityUrl = usePortalVanityUrl();
  const { portalSettingsClusterId, portalSettingsStatus } = useAppSelector(
    (state: RootState) => state.dashboard
  );
  const routeClusterId = decodeLegacyUrlValue(params.clusterId);
  const isWaitingForPortalSettings =
    Boolean(routeClusterId) &&
    (portalSettingsClusterId !== routeClusterId ||
      portalSettingsStatus === "idle" ||
      portalSettingsStatus === "loading");
  const timestamp = useRef(Date.now()).current;
  const destination = isWaitingForPortalSettings
    ? null
    : buildLegacyPaymentLinkRedirectUrl(params, {
        baseUrl: vanityUrl,
        origin: window.location.origin,
        search: location.search,
        hash: location.hash,
        timestamp,
      });

  useEffect(() => {
    if (destination) {
      window.location.replace(destination);
    }
  }, [destination]);

  return isWaitingForPortalSettings || destination ? null : (
    <Navigate
      to={INVALID_PATH}
      replace={true}
      state={invalidRouteState("link")}
    />
  );
};

export default LegacyPaymentLinkRedirect;
